import { NONE_PATH, pathsOf, type StepList, type WorkflowBody, type WorkflowStep } from "@/lib/workflows";

/**
 * Where everything on the builder's canvas sits (ACL-369). The canvas is a board that pans and
 * zooms (`features/workflow-builder.tsx`, React Flow); this only works out positions, so the
 * picture of a workflow is a pure function of its body and is tested as one.
 *
 * The trigger is at the top, each step under the one before with a "+" on the line between, and
 * END under the last. An If/Else's paths sit side by side under it, each a column as wide as its
 * own steps, named at its top, with its own "+" and its own END: the paths never join again. A
 * box that shows a problem gets the room for it. The trigger's column is at x = 0.
 */

export const NODE_WIDTH = 320;
export const NODE_HEIGHT = 76;
/** Room under a box's own text for a problem, two lines at most. */
export const PROBLEM_HEIGHT = 36;
export const ADD_SIZE = 28;
/** Between a box and the "+" on its line, and between the "+" and the next box. */
export const GAP = 22;
export const PATH_WIDTH = 200;
export const PATH_HEIGHT = 32;
export const END_WIDTH = 72;
export const END_HEIGHT = 26;
/** Between two paths side by side. */
export const COLUMN_GAP = 48;

interface Box {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export type LayoutNode =
  | (Box & { kind: "trigger" })
  | (Box & { kind: "step"; step: WorkflowStep })
  /** The "+" that adds a step at `index` of `list`; `after` says where, for a screen reader. */
  | (Box & { kind: "add"; list: StepList; index: number; after: string })
  | (Box & { kind: "path"; ifId: string; key: string; name: string; none: boolean; order: number })
  | (Box & { kind: "end" });

export interface LayoutEdge {
  id: string;
  source: string;
  target: string;
  /** `fork`: from an If/Else to one of its paths. */
  kind: "line" | "fork";
}

export interface Layout {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  width: number;
  height: number;
}

/** The trigger's id on the board. A step's id is lowercase letters, digits and hyphens, so a step
 *  called "trigger" (one made through the API) cannot be taken for it. */
export const TRIGGER_ID = "start:trigger";

const listKey = (list: StepList) => (list === null ? "root" : `${list.ifId}/${list.path}`);

/** Nothing for no steps (an empty path is as wide as its name), a box, or the paths of the
 *  If/Else that ends the list. */
function widthOfList(steps: readonly WorkflowStep[]): number {
  const last = steps[steps.length - 1];
  if (!last) return 0;
  return last.type === "if" ? Math.max(NODE_WIDTH, widthOfPaths(last)) : NODE_WIDTH;
}

function widthOfPath(steps: readonly WorkflowStep[]): number {
  return Math.max(PATH_WIDTH, widthOfList(steps));
}

function widthOfPaths(step: WorkflowStep & { type: "if" }): number {
  const paths = pathsOf(step);
  return paths.reduce((sum, p) => sum + widthOfPath(p.steps), 0) + COLUMN_GAP * (paths.length - 1);
}

export function layout(
  body: WorkflowBody,
  options: { problems?: Record<string, string>; startProblem?: boolean; label?: (step: WorkflowStep) => string } = {},
): Layout {
  const nodes: LayoutNode[] = [];
  const edges: LayoutEdge[] = [];
  const problems = options.problems ?? {};
  const label = options.label ?? ((step: WorkflowStep) => step.id);
  const link = (source: string, target: string, kind: LayoutEdge["kind"] = "line") =>
    edges.push({ id: `${source}->${target}`, source, target, kind });
  const heightOf = (problem: boolean) => NODE_HEIGHT + (problem ? PROBLEM_HEIGHT : 0);

  const add = (list: StepList, index: number, after: string, cx: number, y: number): string => {
    const id = `add:${listKey(list)}:${index}`;
    nodes.push({ kind: "add", id, list, index, after, x: cx - ADD_SIZE / 2, y, width: ADD_SIZE, height: ADD_SIZE });
    return id;
  };

  /** A list of steps from `y` down, centred on `cx`, joined to `from`. The bottom it reached. */
  const place = (steps: readonly WorkflowStep[], list: StepList, cx: number, y: number, from: string): number => {
    let previous = from;
    for (const [i, step] of steps.entries()) {
      const height = heightOf(Boolean(problems[step.id]));
      nodes.push({ kind: "step", id: step.id, step, x: cx - NODE_WIDTH / 2, y, width: NODE_WIDTH, height });
      link(previous, step.id);
      y += height;
      if (step.type !== "if") {
        previous = add(list, i + 1, label(step), cx, y + GAP);
        link(step.id, previous);
        y += GAP + ADD_SIZE + GAP;
        continue;
      }
      // An If/Else ends its list: its paths take the rest of the column, side by side.
      const top = y + GAP * 2;
      let left = cx - widthOfPaths(step) / 2;
      let bottom = top;
      for (const [order, path] of pathsOf(step).entries()) {
        const width = widthOfPath(path.steps);
        const pcx = left + width / 2;
        const pathId = `path:${step.id}/${path.key}`;
        nodes.push({
          kind: "path",
          id: pathId,
          ifId: step.id,
          key: path.key,
          name: path.name,
          none: path.key === NONE_PATH,
          order,
          x: pcx - PATH_WIDTH / 2,
          y: top,
          width: PATH_WIDTH,
          height: PATH_HEIGHT,
        });
        link(step.id, pathId, "fork");
        const pathList = { ifId: step.id, path: path.key };
        const plus = add(pathList, 0, `the start of the "${path.name}" path`, pcx, top + PATH_HEIGHT + GAP);
        link(pathId, plus);
        bottom = Math.max(bottom, place(path.steps, pathList, pcx, top + PATH_HEIGHT + GAP + ADD_SIZE + GAP, plus));
        left += width + COLUMN_GAP;
      }
      return bottom;
    }
    const end = `end:${listKey(list)}`;
    nodes.push({ kind: "end", id: end, x: cx - END_WIDTH / 2, y, width: END_WIDTH, height: END_HEIGHT });
    link(previous, end);
    return y + END_HEIGHT;
  };

  // The workflow's own column is at x = 0 whatever the paths under it do, so a path that grows
  // spreads the board on both sides and never moves the trigger the admin is looking at.
  const width = Math.max(NODE_WIDTH, widthOfList(body.steps));
  const cx = 0;
  const triggerHeight = heightOf(Boolean(options.startProblem));
  nodes.push({ kind: "trigger", id: TRIGGER_ID, x: cx - NODE_WIDTH / 2, y: 0, width: NODE_WIDTH, height: triggerHeight });
  const first = add(null, 0, "the trigger", cx, triggerHeight + GAP);
  link(TRIGGER_ID, first);
  const height = place(body.steps, null, cx, triggerHeight + GAP + ADD_SIZE + GAP, first);
  return { nodes, edges, width, height };
}
